<script setup lang="ts">
  import { onMounted, onUnmounted, type HTMLAttributes } from 'vue'
  import { IconAi, IconLoading } from '@gat-ship/icons'
  import {
    BtnAiGradient,
    DropdownMenuItem,
    Kbd,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    useAppLoading,
    Button
  } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  import type {
    AiExtractDataResponse,
    AiPromptAsset,
    ExtractionTarget,
    PromptTargetType
  } from '../model/types'
  import { LOADING_IDS } from '../constants'
  import { useSmartPaste } from '../composables/useSmartPaste'
  import SmartPasteDialog from './SmartPasteDialog.vue'
  import PromptPresetModalAdd from './PromptPresetModalAdd.vue'
  import PromptPresetModalEdit from './PromptPresetModalEdit.vue'

  interface SmartPasteButtonProps {
    class?: HTMLAttributes['class']
    target: ExtractionTarget
    promptType?: PromptTargetType
    variant?: 'outline' | 'gradient'
    as?: 'button' | 'dropdown-item'
    label?: string
    enableKeyboardShortcut?: boolean
    contextParentId?: string
    additionalPromptText?: string
    autoReadClipboard?: boolean
  }

  const props = withDefaults(defineProps<SmartPasteButtonProps>(), {
    variant: 'gradient',
    as: 'button',
    promptType: 'data-extraction',
    enableKeyboardShortcut: false,
    autoReadClipboard: true
  })

  const emit = defineEmits<{
    (e: 'extracted', data: AiExtractDataResponse): void
  }>()

  const smartPaste = useSmartPaste({
    target: props.target,
    promptType: props.promptType,
    additionalPromptText: props.additionalPromptText,
    autoReadClipboard: props.autoReadClipboard,
    contextParentId: props.contextParentId,
    onExtracted: (data) => emit('extracted', data)
  })

  const { isLoading } = useAppLoading()

  function handleClick() {
    smartPaste.openDialog()
  }

  function handleAddAsset() {
    smartPaste.isDialogOpen.value = false
    smartPaste.promptAssets.openAddPresetModal()
  }

  function handleEditAsset(asset: AiPromptAsset) {
    smartPaste.isDialogOpen.value = false
    smartPaste.promptAssets.openEditPresetModal(asset)
  }

  function handleEditPresetSave(asset: AiPromptAsset) {
    const isVirtual = smartPaste.promptAssets.virtualAssets.value.some(
      (a) => a.id === asset.id
    )
    if (isVirtual) {
      smartPaste.promptAssets.addAsset(asset)
    } else {
      smartPaste.promptAssets.updateAsset(asset)
    }
  }

  async function handleSubmit() {
    await smartPaste.extract()
  }

  if (props.enableKeyboardShortcut) {
    const keydownHandler = async (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key === 'v') {
        if (isLoading(LOADING_IDS.extract)) return
        try {
          smartPaste.promptText.value =
            (await navigator.clipboard.readText()) ?? ''
        } catch {
          // Clipboard access denied
        }
        await smartPaste.extract()
      }
    }

    onMounted(() => document.addEventListener('keydown', keydownHandler))
    onUnmounted(() => document.removeEventListener('keydown', keydownHandler))
  }
</script>

<template>
  <DropdownMenuItem v-if="as === 'dropdown-item'" @click="handleClick">
    <IconAi class="size-4" />
    {{ label ?? 'Smart Paste' }}
  </DropdownMenuItem>
  <TooltipProvider v-else-if="enableKeyboardShortcut">
    <Tooltip>
      <TooltipTrigger as-child>
        <div class="w-fit">
          <BtnAiGradient
            v-if="variant === 'gradient'"
            :class="props.class"
            :label="label ?? 'Smart Paste'"
            :loading="smartPaste.isExtracting.value"
            :disabled="smartPaste.isExtracting.value"
            @click="handleClick"
          />
          <Button
            v-else
            :class="props.class"
            :variant="variant"
            :disabled="smartPaste.isExtracting.value"
            @click="handleClick"
          >
            <IconLoading
              v-if="smartPaste.isExtracting.value"
              class="size-4 shrink-0"
            />
            <IconAi v-else class="size-4" />
            {{ label ?? 'Smart Paste' }}
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" :side-offset="6">
        <Kbd>ctrl + alt + v</Kbd>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
  <template v-else>
    <BtnAiGradient
      v-if="variant === 'gradient'"
      :class="props.class"
      :label="label ?? 'Smart Paste'"
      :loading="smartPaste.isExtracting.value"
      :disabled="smartPaste.isExtracting.value"
      @click="handleClick"
    />
    <Button
      v-else
      :class="props.class"
      :variant="variant"
      :disabled="smartPaste.isExtracting.value"
      @click="handleClick"
    >
      <IconLoading
        v-if="smartPaste.isExtracting.value"
        class="size-4 shrink-0"
      />
      <IconAi v-else class="size-4" />
      {{ label ?? 'Smart Paste' }}
    </Button>
  </template>
  <SmartPasteDialog
    v-model:open="smartPaste.isDialogOpen.value"
    v-model:text="smartPaste.promptText.value"
    v-model:selected-asset-id="smartPaste.selectedAssetId.value"
    v-model:selected-thinking-model="smartPaste.selectedThinkingModel.value"
    :prompt-type="promptType"
    :assets="smartPaste.promptAssets.assets.value"
    :can-manage-assets="smartPaste.promptAssets.canManageAssets.value"
    :can-override-model="smartPaste.promptAssets.canOverrideModel.value"
    :is-extracting="smartPaste.isExtracting.value"
    @submit="handleSubmit"
    @add-asset="handleAddAsset"
    @edit-asset="handleEditAsset"
  />
  <PromptPresetModalAdd
    v-model:open="smartPaste.promptAssets.isAddPresetModalOpen.value"
    :asset="smartPaste.promptAssets.editingAsset.value"
    :target="smartPaste.promptAssets.assets.value"
    :all-assets="smartPaste.promptAssets.assets.value"
    :virtual-assets="smartPaste.promptAssets.virtualAssets.value"
    :target-id="smartPaste.promptAssets.target"
    @add="smartPaste.promptAssets.addAsset($event)"
  />
  <PromptPresetModalEdit
    v-model:open="smartPaste.promptAssets.isEditPresetModalOpen.value"
    :asset="smartPaste.promptAssets.editingAsset.value"
    :target="smartPaste.promptAssets.assets.value"
    :all-assets="smartPaste.promptAssets.assets.value"
    :virtual-assets="smartPaste.promptAssets.virtualAssets.value"
    :target-id="smartPaste.promptAssets.target"
    @update="handleEditPresetSave"
    @remove="smartPaste.promptAssets.removeAsset($event)"
  />
  <AlertDialog v-model:open="smartPaste.promptAssets.showDeleteConfirm.value">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete Asset</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to delete this asset?
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button
          variant="outline"
          @click="smartPaste.promptAssets.confirmDeleteAsset()"
        >
          Confirm
        </Button>
        <Button
          variant="outline"
          @click="smartPaste.promptAssets.showDeleteConfirm.value = false"
        >
          Cancel
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
